import { useCallback, useEffect, useState } from 'react';
import { CalibrationDependency } from 'api';
import { valuationsAction } from 'common/actions';
import { VALIDATED } from 'common/constants/general';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { Calibration as CalibrationValuationModel } from 'common/types/valuation/valuation';
import { Tab } from 'components';
import { VALUATION_SUMMARY_KEY } from '../../common/constants/valuationSummary';
import { useApproachQueryParam } from '../useApproachQueryParam';
import { UseCreateCalibrationParams } from '../useCreateValuation/types';

const useCreateCalibration = (params: UseCreateCalibrationParams) => {
  const {
    valuation,
    REDIRECT_TIMEOUT_DELAY,
    setValuation,
    setPageStatus,
    setArePendingChanges,
    setIsEditingApproachName,
    setIsEditingCalibrationName,
    setIsEditingRangeSensitivityName,
    setIsChangingFinancialStatement,
    handleMenuClose,
    setApproachToUpdate,
    handleOpenUpdateApproachDialog,
  } = params;
  const [, dispatch] = useStore() as unknown as UseStoreValues;
  const [calibrations, setCalibrations] = useState<CalibrationDependency[]>([]);
  const [calibrationToUpdate, setCalibrationToUpdate] = useState<CalibrationValuationModel | null>(null);
  const [calibrationTabs, setCalibrationTabs] = useState<Tab[]>([]);

  // Approach Query Param
  const { setApproachQueryParam } = useApproachQueryParam();

  // Delete Calibration
  const handleDeleteCalibration = useCallback(
    (panelIdParam: string) => {
      if (panelIdParam) {
        valuation.calibrations = valuation.calibrations?.filter(calibration => calibration?.panel_id !== panelIdParam);

        dispatch?.(valuationsAction.setValuationInfo(valuation));
        setValuation(valuation);

        setPageStatus(VALIDATED);
        setArePendingChanges(true);

        setTimeout(() => {
          setApproachQueryParam(VALUATION_SUMMARY_KEY);
        }, REDIRECT_TIMEOUT_DELAY);
      }
    },
    [
      valuation,
      dispatch,
      setValuation,
      setPageStatus,
      setArePendingChanges,
      REDIRECT_TIMEOUT_DELAY,
      setApproachQueryParam,
    ]
  );

  const handleUpdateCalibrationDialog = useCallback(
    (panelIdParam: string) => {
      if (panelIdParam) {
        handleMenuClose();

        const calibrationToBeUpdated = valuation.calibrations?.find(
          calibration => calibration?.panelId === panelIdParam
        );

        if (calibrationToBeUpdated) {
          setApproachToUpdate(null);
          handleOpenUpdateApproachDialog();
          setCalibrationToUpdate(calibrationToBeUpdated);
        }
      }
    },
    [
      handleMenuClose,
      handleOpenUpdateApproachDialog,
      setApproachToUpdate,
      setCalibrationToUpdate,
      valuation.calibrations,
    ]
  );

  const showUpdateCalibrationNameDialog = useCallback(
    (panelIdParam: string) => {
      if (panelIdParam) {
        setIsEditingApproachName(false);
        setIsEditingCalibrationName(true);
        setIsEditingRangeSensitivityName?.(false);
        setIsChangingFinancialStatement(false);
        handleUpdateCalibrationDialog(panelIdParam);
      }
    },
    [
      handleUpdateCalibrationDialog,
      setIsEditingApproachName,
      setIsEditingCalibrationName,
      setIsEditingRangeSensitivityName,
      setIsChangingFinancialStatement,
    ]
  );

  const updateValuationCalibrations = useCallback(
    (updatedCalibration: CalibrationValuationModel[]) => {
      const updatedValuation = { ...valuation, calibrations: updatedCalibration };

      // Set Valuation Info in Store
      dispatch?.(valuationsAction.setValuationInfo(updatedValuation));
      setValuation(updatedValuation);
    },
    [dispatch, setValuation, valuation]
  );

  useEffect(() => {
    if (valuation.calibrations) {
      setCalibrations(valuation.calibrations as CalibrationDependency[]);
    }
  }, [valuation.calibrations]);

  return {
    handleDeleteCalibration,
    showUpdateCalibrationNameDialog,
    updateValuationCalibrations,
    setCalibrationToUpdate,
    calibrationToUpdate,
    setCalibrationTabs,
    calibrationTabs,
    calibrations,
    setCalibrations,
  };
};

export default useCreateCalibration;
